import React, { useState, useRef, useEffect } from 'react';
import IntroductionAudio from '../IntroductionAudio';
import { useVirtualClassAudio } from '../hooks/useVirtualClassAudio';
import { StudentsImage, RecordingTimer, MicButton } from '../components/VirtualClassShared';
import aiStudentsImage from '../../../asset/images/home/ai_students.png';
import './VirtualClassPage.css';

const SeePage = ({ page, lesson, onNext, onCompletionChange }) => {
  const flashcards = page.flashcards || [];
  const students = page.students || [];
  const step = page.step || {};
  const config = page.config || {};
  
  const [currentFlashcardIndex, setCurrentFlashcardIndex] = useState(0);
  const [selectedFlashcard, setSelectedFlashcard] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [completedFlashcards, setCompletedFlashcards] = useState(new Set());
  const [passedFlashcards, setPassedFlashcards] = useState(new Set());
  const [attemptedFlashcards, setAttemptedFlashcards] = useState(new Set());
  const [flashcardResult, setFlashcardResult] = useState({});
  const [flashcardAttempts, setFlashcardAttempts] = useState({});
  const [showStudentResponses, setShowStudentResponses] = useState(false);
  const [studentResponses, setStudentResponses] = useState({});
  const [showConfusion, setShowConfusion] = useState(false);
  const [shouldStopAnimations, setShouldStopAnimations] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [playedReminderFlashcards, setPlayedReminderFlashcards] = useState(new Set());
  const [showStepResult, setShowStepResult] = useState(false);
  const [stepAttempt, setStepAttempt] = useState(1);
  const [stepPassed, setStepPassed] = useState(false);
  const [borderAnimation, setBorderAnimation] = useState(null);
  
  const animationTimeoutRef = useRef(null);
  const reminderAudioRef = useRef(null);
  const attemptNumberRef = useRef({});
  const studentAudioRef = useRef(null);
  
  const {
    isRecording,
    recordingTime,
    isProcessing,
    isPlayingReminder,
    handleStartRecording,
    handleSubmitRecording,
    playUserRecording,
    playReminderAudio: playReminderAudioHook
  } = useVirtualClassAudio({
    page,
    lesson,
    selectedFlashcard,
    exerciseType: 'see',
    maxRecordingTime: config.recordingTimeLimit || 15,
    onResult: handleResult,
    getAttemptNumber: (flashcardId) => attemptNumberRef.current[flashcardId] || 0
  });

  const playStudentResponseAudio = (audioData, onEnded) => {
    try {
      if (!audioData) {
        if (onEnded) onEnded();
        return;
      }

      let audioUrl;
      
      if (typeof audioData === 'string' && (audioData.startsWith('http://') || audioData.startsWith('https://'))) {
        audioUrl = audioData;
      } else if (typeof audioData === 'string' && audioData.length > 0) {
        try {
          const audioBytes = Uint8Array.from(atob(audioData), c => c.charCodeAt(0));
          const audioBlob = new Blob([audioBytes], { type: 'audio/wav' });
          audioUrl = URL.createObjectURL(audioBlob);
        } catch (base64Error) {
          console.error('Error decoding base64 audio:', base64Error);
          if (onEnded) onEnded();
          return;
        }
      } else {
        if (onEnded) onEnded();
        return;
      }
      
      if (studentAudioRef.current) {
        studentAudioRef.current.pause();
        studentAudioRef.current.src = '';
      }
      
      const audio = new Audio(audioUrl);
      studentAudioRef.current = audio;
      setIsPlayingAudio(true);
      
      audio.onended = () => {
        setIsPlayingAudio(false);
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
        if (onEnded) onEnded();
      };
      
      audio.onerror = (error) => {
        console.error('Error playing audio:', error);
        setIsPlayingAudio(false);
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
        if (onEnded) onEnded();
      };
      
      audio.play().catch(error => {
        console.error('Error starting audio playback:', error);
        setIsPlayingAudio(false);
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
        if (onEnded) onEnded();
      });
    } catch (error) {
      console.error('Error playing student response audio:', error);
      setIsPlayingAudio(false);
      if (onEnded) onEnded();
    }
  };

  function handleResult(result, userRecordingUrl) {
    const flashcardId = selectedFlashcard.id;
    const currentAttempt = attemptNumberRef.current[flashcardId] || 0;
    const newAttempt = result.attemptNumber !== undefined ? result.attemptNumber : (currentAttempt + 1);
    
    attemptNumberRef.current[flashcardId] = newAttempt;
    setFlashcardAttempts(prev => ({ ...prev, [flashcardId]: newAttempt }));
    
    if (result.passed || result.is_correct) {
      setFlashcardResult(prev => ({ ...prev, [flashcardId]: 'correct' }));
      setBorderAnimation('green');
      setTimeout(() => {
        setBorderAnimation(null);
      }, 5000);
      
      setShowStudentResponses(true);
      setIsPlayingAudio(true);
      setShouldStopAnimations(false);
      
     
      const customResponseText = selectedFlashcard?.customResponse?.see?.text;
      const responseText = customResponseText || selectedFlashcard?.word || 'word';
      const responses = {};
      students.forEach((student) => {
        responses[student.id] = responseText;
      });
      setStudentResponses(responses);
      
      
      const hasCustomResponse = selectedFlashcard?.customResponse?.see;
     
      const flashcardCustomAudio = selectedFlashcard?.customResponse?.see?.audio?.url || selectedFlashcard?.customResponse?.see?.audio;
      const backendCustomAudio = (hasCustomResponse && (result.student_response_audio || result.studentResponseAudio)) || null;
      const customResponseAudio = flashcardCustomAudio || backendCustomAudio;
      
      setPassedFlashcards(prev => {
        const newPassedSet = new Set([...prev, flashcardId]);
        
        setCompletedFlashcards(prevCompleted => {
          const newCompletedSet = new Set([...prevCompleted, flashcardId]);
          const allCompleted = newCompletedSet.size === flashcards.length;
          
          if (allCompleted) {
            checkCompletion(newCompletedSet, newPassedSet);
          }
          
         
          const navigateToNext = () => {
            if (!allCompleted) {
              setTimeout(() => {
                handleNextFlashcard();
              }, 1500); 
            }
          };
          
          
          if (customResponseAudio) {
            playStudentResponseAudio(customResponseAudio, () => {
              setIsPlayingAudio(false);
              setShouldStopAnimations(true);
              if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
              }
              navigateToNext();
            });
          } else {
            playUserRecording(() => {
              setIsPlayingAudio(false);
              setShouldStopAnimations(true);
              if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
              }
              navigateToNext();
            });
          }
          
          return newCompletedSet;
        });
        
        return newPassedSet;
      });
    } else {
      setFlashcardResult(prev => ({ ...prev, [flashcardId]: 'incorrect' }));
      setBorderAnimation('red');
      setTimeout(() => {
        setBorderAnimation(null);
      }, 3000);
      
      setAttemptedFlashcards(prev => new Set([...prev, flashcardId]));
      
      const isDone = newAttempt >= 2;
      const shouldMoveToNext = newAttempt >= 2;
      
      if (isDone) {
        setCompletedFlashcards(prev => {
          const newSet = new Set([...prev, flashcardId]);
          checkCompletion(newSet);
          return newSet;
        });
      }
      
      const customResponseOnWrong = selectedFlashcard?.customResponse?.see?.onWrong;
      const hasOnWrongResponse = customResponseOnWrong && (
        customResponseOnWrong.audioType !== 'empty' && 
        customResponseOnWrong.audioType !== undefined
      );
      
      if (hasOnWrongResponse) {
        setIsPlayingAudio(true);
        setShouldStopAnimations(false);
        
        const audioType = customResponseOnWrong.audioType || 
                          (customResponseOnWrong.customAudio === 'empty' ? 'empty' : 
                           (customResponseOnWrong.url ? 'url' : 'default'));
        
        const customResponseAudio = audioType === 'url' ? (customResponseOnWrong.url || customResponseOnWrong.audio?.url) : null;
        const ttsText = audioType === 'tts' ? customResponseOnWrong.ttsText : null;
        
        const backendCustomAudio = (customResponseOnWrong && (result.student_response_audio || result.studentResponseAudio)) || null;
        
        if (audioType === 'empty') {
          setIsPlayingAudio(false);
          setShouldStopAnimations(false);
          
          const reminderAudio = getReminderAudio(selectedFlashcard);
          const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
          const shouldPlay = reminderAudio && 
            (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
          
          if (shouldPlay) {
            playReminderAudioHook(reminderAudio);
            
            if (isFlashcardSpecific) {
              setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
            }
          }
        } else if (audioType === 'url' && customResponseAudio) {
          playStudentResponseAudio(customResponseAudio, () => {
            setIsPlayingAudio(false);
            setShouldStopAnimations(false);
            
            const reminderAudio = getReminderAudio(selectedFlashcard);
            const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
            const shouldPlay = reminderAudio && 
              (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
            
            if (shouldPlay) {
              playReminderAudioHook(reminderAudio);
              
              if (isFlashcardSpecific) {
                setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
              }
            }
          });
        } else if (audioType === 'tts') {
          if (backendCustomAudio) {
            playStudentResponseAudio(backendCustomAudio, () => {
              setIsPlayingAudio(false);
              setShouldStopAnimations(false);
              
              const reminderAudio = getReminderAudio(selectedFlashcard);
              const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
              const shouldPlay = reminderAudio && 
                (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
              
              if (shouldPlay) {
                playReminderAudioHook(reminderAudio);
                
                if (isFlashcardSpecific) {
                  setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
                }
              }
            });
          } else {
            setIsPlayingAudio(false);
            setShouldStopAnimations(false);
            
            const reminderAudio = getReminderAudio(selectedFlashcard);
            const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
            const shouldPlay = reminderAudio && 
              (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
            
            if (shouldPlay) {
              playReminderAudioHook(reminderAudio);
              
              if (isFlashcardSpecific) {
                setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
              }
            }
          }
        } else {
          playUserRecording(() => {
            setIsPlayingAudio(false);
            setShouldStopAnimations(false);
            
            const reminderAudio = getReminderAudio(selectedFlashcard);
            const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
            const shouldPlay = reminderAudio && 
              (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
            
            if (shouldPlay) {
              playReminderAudioHook(reminderAudio);
              
              if (isFlashcardSpecific) {
                setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
              }
            }
          });
        }
      } else {
        playUserRecording(() => {
          const reminderAudio = getReminderAudio(selectedFlashcard);
          const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
          const shouldPlay = reminderAudio && 
            (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
          
          if (shouldPlay) {
            playReminderAudioHook(reminderAudio);
            
            if (isFlashcardSpecific) {
              setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
            }
          }
        });
      }
      
      if (shouldMoveToNext) {
        const updatedCompletedSet = new Set([...completedFlashcards, flashcardId]);
        const allCompleted = updatedCompletedSet.size === flashcards.length;
        
        if (!allCompleted) {
          const delay = 3000;
          
          setTimeout(() => {
            handleNextFlashcard();
          }, delay);
        }
      }
    }
  }

  const getReminderAudio = (flashcard) => {
    if (flashcard?.reminderAudio?.see) {
      const seeReminder = flashcard.reminderAudio.see;
      if (seeReminder?.url || (typeof seeReminder === 'string' && seeReminder)) {
        return seeReminder;
      }
    }
    if (step?.reminderAudio) {
      return step.reminderAudio;
    }
    return null;
  };

  const isFlashcardSpecificReminder = (flashcard) => {
    return !!flashcard?.reminderAudio?.see;
  };

  const checkCompletion = (completedSet, passedFlashcardsSet = null) => {
    if (completedSet.size === flashcards.length) {
      const totalFlashcards = flashcards.length;
      const passedSet = passedFlashcardsSet || passedFlashcards;
      const passedCount = Array.from(passedSet).length;
      const percentage = totalFlashcards > 0 ? Math.round((passedCount / totalFlashcards) * 100) : 0;
      const passThreshold = totalFlashcards === 3 ? 66 : (totalFlashcards === 5 ? 80 : (config.passingScore || 80));
      const passed = percentage >= passThreshold;
      
      setStepPassed(passed);
      setShowStepResult(true);
      
      if (onCompletionChange) {
        onCompletionChange(passed);
      }
    }
  };

  const handleFlashcardClick = (flashcard, index) => {
    const canClick = index === currentFlashcardIndex || 
                    (index === currentFlashcardIndex + 1 && attemptedFlashcards.has(flashcards[currentFlashcardIndex]?.id));
    
    if (canClick) {
      setSelectedFlashcard(flashcard);
      setCurrentFlashcardIndex(index);
    }
  };

  const canClickFlashcard = (index) => {
    if (index === currentFlashcardIndex) {
      return true;
    }
    if (index === currentFlashcardIndex + 1) {
      const currentFlashcardId = flashcards[currentFlashcardIndex]?.id;
      return completedFlashcards.has(currentFlashcardId);
    }
    return false;
  };

  const handleStart = () => {
    setIsStarted(true);
    setCurrentFlashcardIndex(0);
    setSelectedFlashcard(null);
    setCompletedFlashcards(new Set());
    setPassedFlashcards(new Set());
    setAttemptedFlashcards(new Set());
    setFlashcardAttempts({});
    setFlashcardResult({});
    setStudentResponses({});
    setShowStudentResponses(false);
    
    if (onCompletionChange) {
      onCompletionChange(false);
    }
  };

  useEffect(() => {
    if (!isStarted || completedFlashcards.size < flashcards.length) {
      if (onCompletionChange) {
        onCompletionChange(false);
      }
    }
  }, [isStarted, completedFlashcards.size, flashcards.length, onCompletionChange]);

  useEffect(() => {
    if (onCompletionChange) {
      onCompletionChange(false);
    }
  }, []);

  const handleNextFlashcard = () => {
    if (currentFlashcardIndex < flashcards.length - 1) {
      const nextIndex = currentFlashcardIndex + 1;
      setCurrentFlashcardIndex(nextIndex);
      setSelectedFlashcard(null);
      setShowStudentResponses(false);
      setShowConfusion(false);
      setBorderAnimation(null);
    }
  };

  const handleMoveToNext = () => {
    if (onNext) {
      onNext();
    }
  };

  const handleRetry = () => {
    setShowStepResult(false);
    setCurrentFlashcardIndex(0);
    setCompletedFlashcards(new Set());
    setPassedFlashcards(new Set());
    setAttemptedFlashcards(new Set());
    setFlashcardAttempts({});
    setFlashcardResult({});
    setShowStudentResponses(false);
    setShowConfusion(false);
    setStepAttempt(2);
    setPlayedReminderFlashcards(new Set());
    setSelectedFlashcard(null);
    setStudentResponses({});
  };

  if (!isStarted) {
    return (
      <div className="text-center">
        {page.introductionAudio && (
          <div className="mb-6">
            <IntroductionAudio
              audio={page.introductionAudio}
              autoPlay={page.introductionAudio.autoPlay}
            />
          </div>
        )}
        
        <div className="mb-6">
          <div className="flex justify-center gap-4 mb-4">
            <div className="bg-blue-500 text-white px-6 py-2 rounded-full font-bold text-lg">SEE</div>
          </div>
          {step.instruction && (
            <p className="text-gray-700 text-lg max-w-2xl mx-auto">{step.instruction}</p>
          )}
        </div>
        <button
          onClick={handleStart}
          className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
        >
          Start
        </button>
      </div>
    );
  }

  return (
    <div className="virtual-class-page">
      <RecordingTimer 
        recordingTime={recordingTime} 
        maxTime={config.recordingTimeLimit || 15}
        isRecording={isRecording}
      />
      
      <div className="mb-2 flex items-center gap-2">
        <div className="bg-blue-500 text-white px-4 py-1.5 rounded-full font-bold text-sm">
          SEE
        </div>
        {step.instruction && (
          <p className="text-gray-700 text-sm">{step.instruction}</p>
        )}
      </div>

      {step.introductionAudio && (
        <div className="mb-2 scale-90 origin-top-left">
          <IntroductionAudio
            audio={step.introductionAudio}
            autoPlay={step.introductionAudio.autoPlay}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-2 mb-2">
            <h3 className="font-bold text-gray-800 mb-2 text-sm">Flashcards</h3>
            <div className="space-y-1.5">
              {flashcards.map((flashcard, index) => {
                const isCurrent = index === currentFlashcardIndex;
                const isCompleted = completedFlashcards.has(flashcard.id);
                const isAttempted = attemptedFlashcards.has(flashcard.id);
                const canClick = canClickFlashcard(index);
                const isSelected = selectedFlashcard?.id === flashcard.id;
                const result = flashcardResult[flashcard.id];
                
                return (
                  <button
                    key={flashcard.id}
                    onClick={() => handleFlashcardClick(flashcard, index)}
                    className={`w-full p-2 text-left border-2 rounded-lg transition-all ${
                      result === 'correct'
                        ? 'border-green-500 bg-green-50'
                        : result === 'incorrect'
                        ? 'border-red-500 bg-red-50'
                        : isSelected
                        ? 'border-red-500 bg-red-50'
                        : isCompleted
                        ? 'border-green-500 bg-green-50 opacity-60'
                        : !canClick
                        ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                        : 'border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                    disabled={!canClick}
                  >
                    <span className="text-lg font-bold">{flashcard.word}</span>
                    {isCurrent && !isSelected && (
                      <span className="ml-2 text-sm text-blue-600 font-semibold">← Click to select</span>
                    )}
                    {isSelected && (
                      <span className="ml-2 text-sm text-red-600 font-semibold">← Selected</span>
                    )}
                    {isCompleted && !isSelected && (
                      <span className="ml-2 text-sm text-green-600 font-semibold">✓ Done</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div>
          <div className="bg-white border border-gray-300 rounded-lg p-2 mb-2">
            <h3 className="font-bold text-gray-800 mb-2 text-sm">Students</h3>
            
            <div className="relative mb-2 flashcard-container">
              <img
                src={page.studentsImage || aiStudentsImage}
                alt="Virtual Students"
                className={`w-full h-auto max-h-64 object-cover rounded-lg shadow-sm ${
                  borderAnimation === 'green' 
                    ? 'border-4 border-green-500' 
                    : borderAnimation === 'red'
                    ? 'border-4 border-red-500'
                    : 'border-4 border-transparent'
                }`}
                style={{
                  animation: borderAnimation === 'green' 
                    ? 'borderPulseGreen 3s ease-in-out' 
                    : borderAnimation === 'red'
                    ? 'borderPulseRed 3s ease-in-out'
                    : 'none'
                }}
              />
              <div 
                className="absolute bottom-2 left-1/2"
                style={{ 
                  minWidth: '160px',
                  minHeight: '100px',
                  perspective: '1000px',
                  perspectiveOrigin: 'center center'
                }}
              >
                {selectedFlashcard ? (
                  <div 
                    className="flashcard-3d flashcard-flipped"
                    style={{
                      width: '100%',
                      height: '100%',
                      transform: 'translateX(-50%) translateZ(50px) rotateY(180deg) rotateX(-5deg) scale(1.05)',
                      transformStyle: 'preserve-3d',
                      animation: 'cardFlipIn 0.6s ease-out'
                    }}
                  >
                    <div 
                      className="flashcard-back"
                      style={{
                        background: 'white',
                        border: '4px solid #9ca3af',
                        borderRadius: '8px',
                        padding: '24px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '220px',
                        minHeight: '140px',
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden'
                      }}
                    >
                      <p 
                        className="text-4xl font-bold"
                        style={{ 
                          transform: 'scaleX(-1)',
                          color: '#1f2937',
                          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {selectedFlashcard.word}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="absolute bottom-0 left-1/2 bg-white border-4 border-gray-300 rounded-lg p-6 text-center shadow-lg"
                    style={{ 
                      transform: 'translateX(-50%)',
                      minWidth: '220px',
                      minHeight: '140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.7
                    }}
                  >
                    <p className="text-gray-400 text-sm">Click a word to show it here</p>
                  </div>
                )}
              </div>
            </div>

            {selectedFlashcard && (
              <div className="mt-2 text-center">
                {isProcessing ? (
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mb-2"></div>
                    <p className="text-xs font-medium text-gray-700">Processing...</p>
                  </div>
                ) : (
                  <MicButton
                    isRecording={isRecording}
                    isProcessing={isProcessing}
                    isPlayingReminder={isPlayingReminder}
                    onStartRecording={handleStartRecording}
                    onStopRecording={handleSubmitRecording}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showStepResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">
              {stepPassed ? 'Step Complete!' : 'Step Results'}
            </h2>
            <p className="text-gray-700 mb-4">
              {stepPassed 
                ? `Great job! You passed with ${Math.round((Array.from(passedFlashcards).length / flashcards.length) * 100)}% correct.`
                : `You got ${Array.from(passedFlashcards).length} out of ${flashcards.length} correct. ${stepAttempt < 2 ? 'You can try again!' : 'You can still continue to the next step.'}`}
            </p>
            <div className="flex gap-4">
              {stepPassed ? (
                <button
                  onClick={handleMoveToNext}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Continue
                </button>
              ) : (
                <>
                  {stepAttempt < 2 ? (
                    <button
                      onClick={handleRetry}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Try Again
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleRetry}
                        className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400"
                      >
                        Retry
                      </button>
                      <button
                        onClick={() => {
                          if (onCompletionChange) {
                            onCompletionChange(true);
                          }
                          handleMoveToNext();
                        }}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Continue Anyway
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeePage;

